import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';

import Browser from '@wetransfer/concorde-browser';

import { AppConfig } from '../../../core-bundle/config/app-config';

import { normalizeTransfer } from '@app/transfer-bundle/helpers/normalize-transfer';
import Analytics from '@module/analytics';
import I18n from '@module/i18n';

import Actions from '@app/store/action';
import { getDirectoriesSelectionState } from '@app/helper/layout';
import { userNeedsTips } from '@app/helper/user';
import { isTransferSizeUnlimited } from '@app/transfer-bundle/helpers';
import { addFiles } from '@app/transfer-bundle/store/actions';

import Poptip from '@component/info/poptip';
import Dropzone from '@component/dropzone';
import ItemList from '@component/itemlist';
import Icon from '@component/icon';

import { DODGER_BLUE } from '@constant/colors';
import { TransferItemAPITypes } from '@app/transfer-bundle/constant/transfer-item-types';
import DIRECTORIES_SELECTION_STATES from '@constant/directories-selection-states';
import { allowedSizeSelector } from '@app/store/selector/transfers';
import { GB } from '@app/constant/sizes';
import {
  getRemainingSize,
  getIsForwardedTransfer,
  getCurrentSize,
} from '@app/transfer-bundle/store/selectors';

import { isConsentFlagActive } from '@component/consent-manager';
import { isValidTCString } from '@module/consent-manager/tc-model';

export class Files extends React.Component {
  directoriesSelectionState = getDirectoriesSelectionState();

  filesInput = React.createRef();
  directoriesInput = React.createRef();

  constructor(props) {
    super(props);

    this.onFilesFocus = this.onFilesFocus.bind(this);
    this.onFilesBlur = this.onFilesBlur.bind(this);
    this.onResetForward = this.onResetForward.bind(this);
    this.showTip = this.showTip.bind(this);
    this.hideTip = this.hideTip.bind(this);
    this.handleAddFilesClick = this.handleAddFilesClick.bind(this);
  }

  // When this component gets unmounted we'll let the
  // "Dropzone" know that it's no longer possible to drop files.
  componentWillUnmount() {
    this.setDropzone(true);
  }

  // When this component gets mounted we'll let the
  // "Dropzone" know that it's possible to drop files now.
  componentDidMount() {
    this.setDropzone();

    if (this.props.fileCount) {
      this.scrollToEndList();
    }
  }

  // In some update scenarios we want to enforce specific behaviour
  componentDidUpdate(prevProps) {
    this.setDropzone();

    // If the fileCount is updated, we always show the 2 (and a half) last added files.
    if (this.node && prevProps.fileCount < this.props.fileCount) {
      this.scrollToEndList();
    }
  }

  setDropzone(forceRemoval) {
    // On mobile we don't provide a panel
    if (!this.props.panel) {
      return;
    }
    // When we're forwarding a transfer, the dropzone should be removed.
    if (forceRemoval || this.props.transfer.parent_id) {
      return Dropzone.removeTarget(this.props.panel);
    }

    return Dropzone.setTarget(this, this.props.panel);
  }

  scrollToEndList() {
    if (!this.node) {
      return;
    }

    const scrollTo = this.node.querySelector(
      '.filelist__item:nth-last-child(2)'
    );
    if (scrollTo) {
      const offset =
        this.props.fileCount > 2 && !isNaN(scrollTo.offsetHeight)
          ? -scrollTo.offsetHeight / 2
          : 0;
      // Wait for a paint to do scrolly stuff
      window.requestAnimationFrame(() =>
        this.props.panelComponent.scrollTo(scrollTo, { offset })
      );
    }
  }

  // This method is called when adding files, either via the
  // input field in this component OR from the Dropzone -
  // the reason that the Dropzone is also handled here is
  // that it gives us one place to manage error while adding
  // files to the list.
  async addFiles(files = []) {
    const { transfer, allowedSize, user } = this.props;

    await this.props.addFiles({
      transfer,
      files,
      allowedSize,
      currentSize: getCurrentSize({ transfer }),
      maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
    });

    // if the only item is a zip file,
    // then show `Bubble` which suggests to upload folder instead
    const newFiles = transfer.files;
    if (newFiles.length === 1 && newFiles[0].type === 'ZIP') {
      this.props.pushTransferNotification({
        text: 'FolderInsteadOfZip',
      });
      return;
    }
  }

  // Handle the event when files are selected on input[type=file]
  // so add the files to the transfer and reset the field to be used
  // again, for even more files!
  onFilesSelected = (event) => {
    if (!event.target.value) {
      return;
    } // empty string

    const files = Array.from(event.target.files).map((file) => {
      file.apiType = TransferItemAPITypes.file;
      file.relativePath = file.webkitRelativePath;
      return file;
    });

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'File Added',
      label: 'button',
    });

    this.addFiles(files);
    this.formNode.reset(); // To reset file input's value (value is an empty string henceforth)
  };

  // Handle the focus/blur events of input[type=file]
  onFilesFocus() {
    if (!this.emptyStateNode) {
      return;
    }
    this.emptyStateNode.classList.add('uploader__empty-state--focused');
  }

  onFilesBlur() {
    if (!this.emptyStateNode) {
      return;
    }
    this.emptyStateNode.classList.remove('uploader__empty-state--focused');
  }

  // This should trigger a click on the input[type=file] field
  // so it opens the file dialog
  onClick = (event) => {
    event.stopPropagation();

    const { onClick } = this.props;

    if (onClick) {
      onClick();
    }

    if (!this.filesInput.current) {
      return;
    }

    this.filesInput.current.click();
  };

  // Reset the forward
  onResetForward() {
    this.props.create();
  }

  showTip() {
    if (userNeedsTips(this.props.user)) {
      this.props.pushTransferNotification({
        text: 'TipFiles',
      });
    }
  }

  hideTip() {
    if (!userNeedsTips(this.props.user)) {
      return;
    }
    this.props.clearTransferNotification();
  }

  renderFilesInput() {
    let tabIndex;
    const {
      isConsentFlagActive,
      isConsentStringValid,
      acceptedTandC,
    } = this.props;

    // If the user is part of the cookiewall rollout and does not have a valid
    // consent string, this means the user has not interacted with the consent
    // wall and thus we shouldn't let them reach the input button.
    //
    // At this point, the user has already accepted the T and C.
    if (isConsentFlagActive && !isConsentStringValid) {
      tabIndex = '-1';
    }

    // If the user is not part of the cookiewall rollout and has not accepted the T and C,
    // then we shouldn't let the user reach the input button either.
    if (!isConsentFlagActive && !acceptedTandC) {
      tabIndex = '-1';
    }

    return (
      <input
        ref={this.filesInput}
        type="file"
        multiple
        tabIndex={tabIndex}
        onChange={this.onFilesSelected}
        onFocus={this.onFilesFocus}
        onBlur={this.onFilesBlur}
      />
    );
  }

  renderDirectoriesInput() {
    return (
      <input
        tabIndex="-1"
        ref={this.directoriesInput}
        type="file"
        multiple
        webkitdirectory="webkitdirectory"
        directory="directory"
        onChange={this.onFilesSelected}
      />
    );
  }

  triggerDirectoriesSelection = (event) => {
    event.stopPropagation();

    const { onClick } = this.props;

    if (onClick) {
      onClick();
    }

    if (!this.directoriesInput.current) {
      return;
    }

    this.directoriesInput.current.click();
  };

  renderDirectoriesSelector() {
    return (
      <button
        tabIndex={this.props.acceptedTandC ? null : '-1'}
        className="uploader__sub-title uploader__directories-dialog-trigger"
        onClick={this.triggerDirectoriesSelection}
      >
        {I18n.t('transfer.form.select_folder')}
      </button>
    );
  }

  renderDropDown = () => {
    return (
      <div className="menu">
        <button className="menu__item" onClick={this.onClick}>
          {I18n.t('transfer.add_items.files')}
        </button>
        <button
          className="menu__item"
          onClick={this.triggerDirectoriesSelection}
        >
          {I18n.t('transfer.add_items.folders')}
        </button>
      </div>
    );
  };

  handleAddFilesClick(event) {
    if (
      this.directoriesSelectionState === DIRECTORIES_SELECTION_STATES.COMBINED
    ) {
      this.triggerDirectoriesSelection(event);
      return;
    }

    this.onClick(event);

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_add_file',
    });
  }

  // This render method is called if there are 0 files in the transfer
  renderEmptyState(allowedSize, remainingSize) {
    const { user } = this.props;
    const classNames = classnames('uploader__empty-state', {
      'uploader__empty-state--with-directories-selector':
        this.directoriesSelectionState ===
        DIRECTORIES_SELECTION_STATES.SEPARATED,
    });

    let fileSizeLimitText = (
      <p className="uploader__sub-title">
        {I18n.t('transfer.form.available_space', {
          size: I18n.size(remainingSize),
        })}
      </p>
    );

    const isUnlimitedStorage = user.rules.storageLimit === 500 * GB;

    if (isTransferSizeUnlimited(allowedSize) || isUnlimitedStorage) {
      fileSizeLimitText = (
        <p className="uploader__sub-title">
          {I18n.t('transfer.form.drop_files')}
        </p>
      );

      if (Browser.isMobile) {
        fileSizeLimitText = null;
      }
    }

    return (
      <div
        ref={(node) => (this.emptyStateNode = node)}
        className={classNames}
        onClick={this.handleAddFilesClick}
        onMouseEnter={this.showTip}
        onMouseLeave={this.hideTip}
      >
        <Icon type="PlusCircle" />
        <h2>{I18n.t('transfer.form.add_files')}</h2>
        {this.directoriesSelectionState ===
        DIRECTORIES_SELECTION_STATES.SEPARATED
          ? this.renderDirectoriesSelector()
          : fileSizeLimitText}
      </div>
    );
  }

  // This render method is called if there are 1+ files in the transfer
  renderSummary(transfer, allowedSize, remainingSize, currentSize) {
    let addMoreButton = (
      <Icon type="Add" color={DODGER_BLUE} className="uploader__add-files" />
    );
    let addMoreLabel = (
      <span className="uploader__add-files-label" onClick={this.onClick}>
        {I18n.t('transfer.form.more_files')}
      </span>
    );
    let onClick =
      this.directoriesSelectionState === DIRECTORIES_SELECTION_STATES.COMBINED
        ? this.triggerDirectoriesSelection
        : this.onClick;
    let precision = 1;
    let filesCountLabel = I18n.t('transfer.form.num_files', {
      remaining: I18n.size(remainingSize, {
        precision,
      }),
      count: transfer.files.length,
    });

    // For transfers we don't show the 'add more files' button
    if (this.props.isForwardedTransfer) {
      addMoreButton = (
        <Icon
          type="Add"
          color={DODGER_BLUE}
          className="uploader__add-files uploader__add-files--reset"
          onClick={this.onResetForward}
        />
      );
      addMoreLabel = (
        <span
          className="uploader__add-files-label"
          onClick={this.onResetForward}
        >
          {I18n.t('transfer.form.reset_forward')}
        </span>
      );
      onClick = null;
    }

    if (remainingSize <= AppConfig.transfer.precisionThreshold) {
      precision = 0;
    }

    if (isTransferSizeUnlimited(allowedSize)) {
      filesCountLabel = I18n.t('transfer.form.files_count', {
        count: transfer.files.length,
        fileSize: I18n.size(currentSize, {
          precision,
        }),
      });
    }

    let footerContent = (
      <div className="uploader__add-more" onClick={onClick}>
        {addMoreButton}
        <div className="uploader__add-files-content">
          {addMoreLabel}
          <span className="uploader__add-files-count">{filesCountLabel}</span>
        </div>
      </div>
    );
    if (
      !this.props.isForwardedTransfer &&
      this.directoriesSelectionState === DIRECTORIES_SELECTION_STATES.SEPARATED
    ) {
      footerContent = (
        <Poptip
          content={this.renderDropDown()}
          trigger="click"
          anchor="bottom-left"
          className="uploader__add-items-dropdown"
        >
          {footerContent}
        </Poptip>
      );
    }

    return (
      <div className="uploader__list">
        <ItemList transfer={transfer} removableItems />
        <div className="uploader__footer">{footerContent}</div>
      </div>
    );
  }

  renderInputs() {
    switch (this.directoriesSelectionState) {
      case DIRECTORIES_SELECTION_STATES.SEPARATED:
        return (
          <React.Fragment>
            {this.renderFilesInput()}
            {this.renderDirectoriesInput()}
          </React.Fragment>
        );
      case DIRECTORIES_SELECTION_STATES.COMBINED:
        return this.renderDirectoriesInput();
      default:
        return this.renderFilesInput();
    }
  }

  // Render the list of files.
  render() {
    const { transfer, allowedSize, channel, user } = this.props;

    // Doesn't work in mapStateToProps because the transfer is mutated
    const remainingSize = getRemainingSize({ transfer, channel, user });

    // This is the only component where we directly call the normalizeTransfer method.
    // Thing is, is that our api still expects the file object, but all the child components expect items.
    const normalizedTransfer = normalizeTransfer(transfer);

    // What are we going to display?
    let component = null;
    const currentSize = getCurrentSize({ transfer });
    if (!normalizedTransfer.items.length) {
      component = this.renderEmptyState(allowedSize, remainingSize);
    } else {
      component = this.renderSummary(
        normalizedTransfer,
        allowedSize,
        remainingSize,
        currentSize
      );
    }

    return (
      <div ref={(node) => (this.node = node)} className="uploader__files">
        <form ref={(node) => (this.formNode = node)}>
          {this.renderInputs()}
        </form>
        {component}
      </div>
    );
  }
}

Files.propTypes = {
  addFiles: PropTypes.func.isRequired,
  clearTransferNotification: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  fileCount: PropTypes.number,
  notification: PropTypes.object,
  onClick: PropTypes.func,
  panel: PropTypes.object,
  panelComponent: PropTypes.object,
  pushTransferNotification: PropTypes.func.isRequired,
  transfer: PropTypes.object,
  user: PropTypes.object.isRequired,
  account: PropTypes.object,
  channel: PropTypes.object,
  allowedSize: PropTypes.number,
  isForwardedTransfer: PropTypes.bool,
  acceptedTandC: PropTypes.bool,
  isConsentFlagActive: PropTypes.bool,
  isConsentStringValid: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  notification: state.notification,
  transfer: state.transfer,
  user: state.user,
  channel: state.channel,
  account: state.account,
  acceptedTandC: state.session.tandc,
  allowedSize: allowedSizeSelector(state),
  isForwardedTransfer: getIsForwardedTransfer(state),
  isConsentFlagActive: isConsentFlagActive(),
  isConsentStringValid: isValidTCString(state.consent.tcString),
});

const mapDispatchToProps = {
  addFiles,
  create: Actions.Transfer.create,
  clearTransferNotification: Actions.Notification.clearTransfer,
  pushTransferNotification: Actions.Notification.pushTransfer,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  pure: false,
})(Files);
